import React from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Stack,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import {
  PERFORMANCE_RANGE_OPTIONS,
  MONTH_ORDER,
  getTargetMonths,
  fmtMillion,
  type PerformanceRange,
  type TrialBalanceRow,
} from './constants';

type Props = {
  dedupedRows: TrialBalanceRow[];
  performanceRange: PerformanceRange;
  currentMonth: number;
  /** 期間セレクタをこのセクションのヘッダーに表示するか (経営概況ページのみ true) */
  showRangeSelector?: boolean;
  setPerformanceRange?: (range: PerformanceRange) => void;
  setCurrentMonth?: (m: number) => void;
};

/**
 * 業績サマリーダッシュボード（売上高・売上総利益・販管費・営業利益の4カード）。
 * 業績範囲は showRangeSelector=true のとき本セクションのヘッダーで操作でき、損益計算書・部署別ダッシュボードと共有される。
 * false (既定) の場合は損益計算書テーブル側のセレクタで操作され、本セクションはその範囲に追従する。
 */
export default function PerformanceSummaryCards({
  dedupedRows,
  performanceRange,
  currentMonth,
  showRangeSelector = false,
  setPerformanceRange,
  setCurrentMonth,
}: Props) {
  // 選択中の業績範囲の対象月 (単月=選択月のみ, 通期=12ヶ月, 各四半期=3ヶ月)
  const targetMonths = getTargetMonths(performanceRange, currentMonth);
  // 対象月分の合計を返す (suffix='actual' | 'budget' | 'actual_prev')
  const getYearTotal = (category: string, suffix: 'actual' | 'budget' | 'actual_prev' = 'actual') => {
    return dedupedRows
      .filter(r => r.category_l2_name === category)
      .reduce(
        (sum, r) => sum + targetMonths.reduce(
          (msum, m) => msum + (Number(r[`month_${m}_${suffix}`]) || 0),
          0,
        ),
        0,
      );
  };

  // 工事売上高 (account_code 4101D) の実績を選択範囲で合算 (売上高カードの工事高表示用)
  const getKojiActual = () => {
    return dedupedRows
      .filter(r => r.account_code === '4101D')
      .reduce(
        (sum, r) => sum + targetMonths.reduce(
          (msum, m) => msum + (Number(r[`month_${m}_actual`]) || 0),
          0,
        ),
        0,
      );
  };

  // 範囲実績合計
  const salesActual = getYearTotal('純売上高');
  const cogsActual = getYearTotal('完成工事原価');
  const sgaActual = getYearTotal('販売費及び一般管理費計');
  const grossActual = salesActual - cogsActual;
  const opActual = grossActual - sgaActual;

  // 通期予算合計
  const salesBudget = getYearTotal('純売上高', 'budget');
  const cogsBudget = getYearTotal('完成工事原価', 'budget');
  const sgaBudget = getYearTotal('販売費及び一般管理費計', 'budget');
  const grossBudget = salesBudget - cogsBudget;
  const opBudget = grossBudget - sgaBudget;

  // 通期前年実績合計
  const salesPrev = getYearTotal('純売上高', 'actual_prev');
  const cogsPrev = getYearTotal('完成工事原価', 'actual_prev');
  const sgaPrev = getYearTotal('販売費及び一般管理費計', 'actual_prev');
  const grossPrev = salesPrev - cogsPrev;
  const opPrev = grossPrev - sgaPrev;

  // 通期予算達成率 (実績 / 予算 × 100)
  const getAchievementRate = (actual: number, budget: number) => {
    if (budget === 0) return 0;
    return (actual / Math.abs(budget)) * 100;
  };
  // 前年比 (当年実績 / 前年実績 × 100) 前年が 0 なら null
  const getYoyRate = (actual: number, prev: number): number | null => {
    if (prev === 0) return null;
    return (actual / Math.abs(prev)) * 100;
  };
  // 売上構成比 (実績) (各指標実績 / 売上高実績 × 100) 売上高実績が 0 なら null
  const getSalesRatio = (v: number): number | null => {
    if (salesActual === 0) return null;
    return (v / salesActual) * 100;
  };
  // 売上構成比 (計画) (各指標予算 / 売上高予算 × 100) 売上高予算が 0 なら null
  const getBudgetSalesRatio = (v: number): number | null => {
    if (salesBudget === 0) return null;
    return (v / salesBudget) * 100;
  };

  // 工事高 (工事売上高実績) と、売上高に占める工事高比率 (売上高カードのみで表示)
  const kojiActual = getKojiActual();
  const kojiRate = salesActual !== 0 ? (kojiActual / salesActual) * 100 : null;

  const dashboardItems = [
    { title: '売上高',   value: salesActual, budget: salesBudget, prev: salesPrev, rate: getAchievementRate(salesActual, salesBudget), yoy: getYoyRate(salesActual, salesPrev), showSalesRatio: false, salesRatio: null,                      budgetSalesRatio: null,                          showKoji: true,  koji: kojiActual, kojiRate, icon: <Icons.Payments sx={{ fontSize: '18px' }} />,              color: '#eff6ff', iconColor: '#3b82f6' },
    { title: '売上総利益', value: grossActual, budget: grossBudget, prev: grossPrev, rate: getAchievementRate(grossActual, grossBudget), yoy: getYoyRate(grossActual, grossPrev), showSalesRatio: true,  salesRatio: getSalesRatio(grossActual), budgetSalesRatio: getBudgetSalesRatio(grossBudget), showKoji: false, koji: 0,          kojiRate: null,    icon: <Icons.DonutLarge sx={{ fontSize: '18px' }} />,             color: '#f5f3ff', iconColor: '#8b5cf6' },
    { title: '販管費',   value: sgaActual,   budget: sgaBudget,   prev: sgaPrev,   rate: getAchievementRate(sgaActual,   sgaBudget),   yoy: getYoyRate(sgaActual,   sgaPrev),   showSalesRatio: true,  salesRatio: getSalesRatio(sgaActual),   budgetSalesRatio: getBudgetSalesRatio(sgaBudget),   showKoji: false, koji: 0,          kojiRate: null,    icon: <Icons.ShowChart sx={{ fontSize: '18px' }} />,              color: '#eff6ff', iconColor: '#3b82f6' },
    { title: '営業利益', value: opActual,    budget: opBudget,    prev: opPrev,    rate: getAchievementRate(opActual,    opBudget),    yoy: getYoyRate(opActual,    opPrev),    showSalesRatio: true,  salesRatio: getSalesRatio(opActual),    budgetSalesRatio: getBudgetSalesRatio(opBudget),    showKoji: false, koji: 0,          kojiRate: null,    icon: <Icons.AccountBalanceWallet sx={{ fontSize: '18px' }} />, color: '#eff6ff', iconColor: '#3b82f6' },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {/* セクションの主タイトル。損益計算書はこの補足の位置付けのため、こちらをアイコン付き・太字で強調する */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', p: 0.7, borderRadius: '8px', backgroundColor: '#eff6ff', color: '#3b82f6' }}>
            <Icons.Assessment sx={{ fontSize: '22px' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
            {performanceRange === 'annual'
              ? '通期業績'
              : performanceRange === 'month'
                ? `${currentMonth}月 業績`
                : `${PERFORMANCE_RANGE_OPTIONS.find((o) => o.value === performanceRange)?.label ?? performanceRange} 業績`}
          </Typography>
        </Box>

        {/* 期間セレクタ — 単月(月セレクト) / 四半期 / 半期 / 通期を1本のラジオに統合。
            この選択は損益計算書・部署別ダッシュボードと共有され、同時に切り替わる */}
        {showRangeSelector && setPerformanceRange && setCurrentMonth && (
          <RadioGroup
            row
            value={performanceRange}
            onChange={(e) => setPerformanceRange(e.target.value as PerformanceRange)}
            sx={{ gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}
          >
            {PERFORMANCE_RANGE_OPTIONS.map(opt => (
              <React.Fragment key={opt.value}>
                <FormControlLabel
                  value={opt.value}
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={opt.label}
                  sx={{ mr: 0.25, '& .MuiFormControlLabel-label': { fontSize: '13px', fontWeight: 600, color: '#475569' } }}
                />
                {/* 単月ラジオの直後に月セレクトを配置。単月選択時のみ有効 */}
                {opt.value === 'month' && (
                  <FormControl size="small" sx={{ minWidth: 84, mr: 2 }}>
                    <Select
                      value={currentMonth}
                      onChange={(e) => setCurrentMonth(Number(e.target.value))}
                      disabled={performanceRange !== 'month'}
                      sx={{ height: '30px', backgroundColor: '#fff' }}
                    >
                      {MONTH_ORDER.map(m => (
                        <MenuItem key={m} value={m}>
                          {m}月
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </React.Fragment>
            ))}
          </RadioGroup>
        )}
      </Box>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2,
      }}>
        {dashboardItems.map((item, idx) => (
          <Card key={idx} sx={{ borderRadius: '10px', boxShadow: '0 2px 4px -1px rgb(0 0 0 / 0.08)', border: '1px solid #f1f5f9' }}>
            <CardContent sx={{ px: 1.75, py: 2.75, '&:last-child': { pb: 2.75 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{ p: 0.7, borderRadius: '8px', backgroundColor: item.color, color: item.iconColor, display: 'flex' }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#444444', lineHeight: 1.3 }}>
                      {item.title}
                    </Typography>
                  </Box>
                  {/* 計画値 (予算) */}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.25 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', lineHeight: 1 }}>
                      計画
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#64748b', lineHeight: 1 }}>
                      {fmtMillion(item.budget)}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8', lineHeight: 1 }}>
                      百万
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '22px', fontWeight: 800, color: item.value < 0 ? '#ef4444' : '#1e293b', lineHeight: 1.8 }}>
                      {fmtMillion(item.value)}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>
                      百万
                    </Typography>
                  </Box>
                  {/* 計画との差額 (実績 − 計画) */}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.25 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', lineHeight: 1 }}>
                      計画差
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: (item.value - item.budget) >= 0 ? '#059669' : '#ef4444', lineHeight: 1 }}>
                      {(item.value - item.budget) >= 0 ? '+' : ''}{fmtMillion(item.value - item.budget)}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8', lineHeight: 1 }}>
                      百万
                    </Typography>
                  </Box>
                  {/* 前年実績 */}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.25 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', lineHeight: 1 }}>
                      前年
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#64748b', lineHeight: 1 }}>
                      {fmtMillion(item.prev)}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8', lineHeight: 1 }}>
                      百万
                    </Typography>
                  </Box>
                  {/* 工事高 (売上高カードのみ。工事売上高の額面と売上に占める割合。工事高ゼロなら非表示) */}
                  {item.showKoji && item.koji !== 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.25, py: 0.25, flexWrap: 'nowrap', minWidth: 0 }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', lineHeight: 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        工事高
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: item.koji < 0 ? '#ef4444' : '#64748b', lineHeight: 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {fmtMillion(item.koji)}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8', lineHeight: 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        百万
                      </Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: item.iconColor, lineHeight: 1, ml: 0.25, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        ({item.kojiRate === null ? '-' : `${item.kojiRate.toFixed(1)}%`})
                      </Typography>
                    </Box>
                  )}
                  {/* 売上構成比 (売上総利益・販管費・営業利益のみ。実績 + 計画を併記。売上高が 0 の場合は - 表示) */}
                  {item.showSalesRatio && (
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.25, py: 0.25, flexWrap: 'wrap' }}>
                      {/* 実績構成比 (グループ内は折り返さない) */}
                      <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5, whiteSpace: 'nowrap' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', lineHeight: 1 }}>
                          構成比
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#64748b', lineHeight: 1 }}>
                          {item.salesRatio === null ? '-' : item.salesRatio.toFixed(1)}
                        </Typography>
                        {item.salesRatio !== null && (
                          <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8', lineHeight: 1 }}>
                            ％
                          </Typography>
                        )}
                      </Box>
                      {/* 計画構成比 (予算ベース。グループ内は折り返さない) */}
                      <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5, whiteSpace: 'nowrap' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#cbd5e1', lineHeight: 1 }}>
                          計画
                        </Typography>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#94a3b8', lineHeight: 1 }}>
                          {item.budgetSalesRatio === null ? '-' : item.budgetSalesRatio.toFixed(1)}
                        </Typography>
                        {item.budgetSalesRatio !== null && (
                          <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#cbd5e1', lineHeight: 1 }}>
                            ％
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
                <Stack direction="column" alignItems="flex-end" spacing={0.6}>
                  {/* 予算達成率 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.2,
                      px: 0.7,
                      py: 0.2,
                      borderRadius: '4px',
                      backgroundColor: item.rate >= 100 ? '#ecfdf5' : '#fef2f2',
                      color: item.rate >= 100 ? '#059669' : '#ef4444'
                    }}>
                      {item.rate >= 100 ? <Icons.TrendingUp sx={{ fontSize: '15px' }} /> : <Icons.TrendingDown sx={{ fontSize: '15px' }} />}
                      <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                        {item.rate.toFixed(1)}%
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#94a3b8', lineHeight: 1, mt: 0.2 }}>
                      vs 計画
                    </Typography>
                  </Box>
                  {/* 前年比 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.2,
                      px: 0.7,
                      py: 0.2,
                      borderRadius: '4px',
                      backgroundColor: item.yoy === null ? '#f1f5f9' : (item.yoy >= 100 ? '#ecfdf5' : '#fef2f2'),
                      color: item.yoy === null ? '#94a3b8' : (item.yoy >= 100 ? '#059669' : '#ef4444')
                    }}>
                      {item.yoy !== null && (item.yoy >= 100 ? <Icons.TrendingUp sx={{ fontSize: '15px' }} /> : <Icons.TrendingDown sx={{ fontSize: '15px' }} />)}
                      <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                        {item.yoy === null ? '-' : `${item.yoy.toFixed(1)}%`}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#94a3b8', lineHeight: 1, mt: 0.2 }}>
                      vs 前年
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
